import { Alert, Card, CardContent, Chip, IconButton, Stack, Typography } from '@mui/joy';
import { Circle, Info, X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AttributeValueRows,
  ColourEditor,
  getAttributeValueKey,
  IAttributeValueField,
  NumberRangeEditor,
  ValuesEditor,
} from 'companySettings/components/PartFinderAttributeValues';
import { attributeTypeLabelKey } from 'companySettings/constants';
import { AttributeType } from 'companySettings/enums';
import {
  IPartFinderAttribute,
  IPartFinderAttributeValue,
  IPartFinderProductTypeAttribute,
} from 'companySettings/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { getEmptyErrorMessage } from 'core/yup';
import { Color, Direction, Level, Orientation, Size, Variant } from 'theme/enums';

import {
  AttributeValueSource,
  IInheritedAttributeSource,
  IUpsertProductTypeAttributeFormData,
} from './types';
import { copyListValues, isValueInList } from './utils';

const toListId = (listId: number | string | null | undefined) => {
  const id = Number(listId);

  return listId && Number.isInteger(id) && id > 0 ? id : null;
};

interface IProps {
  attribute?: IPartFinderProductTypeAttribute;
  inheritedSource?: IInheritedAttributeSource;
  lists: IPartFinderAttribute[];
  attributeNames: string[];
  existingNames: string[];
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IUpsertProductTypeAttributeFormData>) => void;
  onSubmit: (
    reset: UseFormReset<IUpsertProductTypeAttributeFormData>
  ) => (data: IUpsertProductTypeAttributeFormData) => Promise<void>;
}

export const UpsertProductTypeAttributeFormFields = ({
  attribute,
  inheritedSource,
  lists,
  attributeNames,
  existingNames,
  isOpen,
  onClose,
  onSubmit,
}: IProps) => {
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  // The list whose values the form already holds, so picking it again copies nothing.
  const appliedListIdRef = useRef<number | null>(attribute?.sourceAttributeId ?? null);

  const { t } = useTranslation();

  const { control, errors, watch, setValue, setError, clearErrors } = useFormContext();
  const { fields, append, move, remove } = useFieldArray({ control, name: 'values' });

  const [name, source, type, sourceAttributeId, saveAsList]: [
    string,
    AttributeValueSource,
    AttributeType,
    number | string | null,
    boolean,
  ] = watch(['name', 'source', 'type', 'sourceAttributeId', 'saveAsList']);

  const valueFields = fields as IAttributeValueField[];
  const hasValues = valueFields.length > 0;
  const isListMode = AttributeValueSource.VALUE_LIST === source;
  // The server fixes a saved attribute's type, so another type needs a new attribute.
  const isSavedAttribute = Boolean(attribute?.id);

  // An attribute a family inherits: its values are compared with the product type's instead of a
  // list's, and only they can change.
  const isInherited = Boolean(inheritedSource);

  const selectedListId = toListId(sourceAttributeId);
  const selectedList = lists.find((list) => list.id === selectedListId);
  const listValues = isInherited ? inheritedSource.values : selectedList?.values ?? [];
  const isNewValue = (value: IPartFinderAttributeValue) => !isValueInList(type, value, listValues);
  const showsNewValues = isInherited || (isListMode && Boolean(selectedList));
  // Read from the rows on screen, the same ones the New badges are drawn on.
  const hasNewValues = showsNewValues && valueFields.some(isNewValue);

  const sourceListOptions = isSavedAttribute
    ? lists.filter((list) => list.type === attribute.type)
    : lists;

  const typeOptions = Object.values(AttributeType).map((attributeType) => ({
    value: attributeType,
    label: t(attributeTypeLabelKey[attributeType]),
  }));

  const getListLabel = (list: IPartFinderAttribute) =>
    t('labelValueListOption', { name: list.name, type: t(attributeTypeLabelKey[list.type]) });

  // Names used on other product types, for consistent naming. This product type's own attributes
  // are left out: the same name twice is refused anyway.
  const takenNames = new Set(
    existingNames.map((existingName) => existingName.trim().toLowerCase())
  );
  const nameOptions: IAutocompleteOption[] = attributeNames
    .filter((attributeName) => !takenNames.has(attributeName.trim().toLowerCase()))
    .map((attributeName) => ({ name: attributeName }));

  const onPickName = (option?: IAutocompleteOption) => {
    setValue('name', option?.name ?? '');
    clearErrors('name');
  };

  const onAddValue = (value: IPartFinderAttributeValue) => {
    const valueKey = getAttributeValueKey(type, value);

    if (valueFields.some((field) => valueKey === getAttributeValueKey(type, field))) {
      return false;
    }

    append(value);

    return true;
  };

  const handleOnSubmit =
    (reset: UseFormReset<IUpsertProductTypeAttributeFormData>) =>
    (data: IUpsertProductTypeAttributeFormData) => {
      const trimmedName = data.name?.trim().toLowerCase();

      if (existingNames.some((existingName) => existingName.trim().toLowerCase() === trimmedName)) {
        setError('name', {
          type: 'validate',
          message: t('errorPartFinderProductTypeAttributeExists'),
        });

        return;
      }

      // A list-backed attribute needs its list, of its own type.
      if (AttributeValueSource.VALUE_LIST === data.source) {
        const list = lists.find((candidate) => candidate.id === toListId(data.sourceAttributeId));

        if (!list || list.type !== data.type) {
          setError('sourceAttributeId', {
            type: list ? 'validate' : 'required',
            message: list
              ? t('errorPartFinderProductTypeAttributeListTypeMismatch')
              : getEmptyErrorMessage(t('labelValueList')),
          });

          return;
        }
      }

      if (!data.values?.length) {
        setError('values', {
          type: 'min',
          message: t('errorPartFinderProductTypeAttributeValuesRequired', { name: data.name }),
        });

        return;
      }

      if (
        AttributeValueSource.TYPED === data.source &&
        data.saveAsList &&
        !data.newListName?.trim()
      ) {
        setError('newListName', {
          type: 'required',
          message: getEmptyErrorMessage(t('labelNameValueList')),
        });

        return;
      }

      return onSubmit(reset)(data);
    };

  // The drawer reopens on another attribute: its own list is the one already applied.
  useEffect(() => {
    if (isOpen) {
      appliedListIdRef.current = attribute?.sourceAttributeId ?? null;
      setIsBannerDismissed(false);
    }
  }, [isOpen, attribute]);

  // Picking another list copies its published values in and locks the type to the list's.
  useEffect(() => {
    if (!isListMode || selectedListId === appliedListIdRef.current) {
      return;
    }

    appliedListIdRef.current = selectedListId;

    if (!selectedList) {
      return;
    }

    setValue('type', selectedList.type);
    setValue('values', copyListValues(selectedList.values));
    setValue('sourceVersionNumber', selectedList.versionNumber ?? null);
    setValue('updateList', false);
  }, [isListMode, selectedList, selectedListId, setValue]);

  // With no value left that the list lacks, there is nothing to write back. Only once the list has
  // loaded: before that, a pending write-back of a reopened attribute would be lost.
  useEffect(() => {
    if (selectedList && !hasNewValues) {
      setValue('updateList', false);
    }
  }, [hasNewValues, selectedList, setValue]);

  // The list name only counts while the box is ticked, so its error goes with the tick.
  useEffect(() => {
    if (!saveAsList) {
      clearErrors('newListName');
    }
  }, [clearErrors, saveAsList]);

  return (
    <Form
      title={attribute ? t('titleEditAttribute') : t('titleNewAttribute')}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleOnSubmit}
      submitBtnLabel={t('buttonLabelSaveAttributes')}
      cancelBtnLabel={t('buttonLabelCancel')}
      disabledSubmit={!name?.trim() || !hasValues}
      level={1}
    >
      <FormField
        name="name"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          error: errors?.name,
          label: t('labelName'),
          required: true,
          disabled: isInherited,
          autoFocus: !isInherited,
          optionKey: 'name',
          value: name ? { name } : undefined,
          defaultOptions: nameOptions,
          getKey: (option: IAutocompleteOption) => option?.name,
          getLabel: (option: IAutocompleteOption) => option?.name ?? '',
          hasCreateOption: true,
          disableClearable: false,
          onChange: (_, option: IAutocompleteOption) => onPickName(option),
          onNewOption: onPickName,
          renderOptions: (option: IAutocompleteOption) => (
            <Stack
              direction={Direction.ROW}
              sx={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography level={Level.BODY_MD}>{option.name}</Typography>
              <Chip
                size={Size.SM}
                variant={Variant.SOFT}
                color={Color.PRIMARY}
                startDecorator={<Circle weight="fill" size={8} />}
              >
                {t('labelAttributeInUse')}
              </Chip>
            </Stack>
          ),
          renderNewOption: (option: IAutocompleteOption) => (
            <Typography level={Level.TITLE_SM} color={Color.PRIMARY}>
              {t('labelCreateAttributeName', { name: option.name })}
            </Typography>
          ),
        }}
      />

      <Card
        variant={Variant.OUTLINED}
        color={Color.PRIMARY}
        sx={{ backgroundColor: 'neutral.100' }}
      >
        <CardContent>
          <Stack gap={2}>
            {!isInherited && (
              <FormField
                name="source"
                type={InputType.RADIO_GROUP}
                inputProps={{
                  control,
                  error: errors?.source,
                  orientation: Orientation.HORIZONTAL,
                  choices: [
                    { value: AttributeValueSource.TYPED, label: t('labelTypeValuesHere') },
                    { value: AttributeValueSource.VALUE_LIST, label: t('labelUseValueList') },
                  ],
                }}
              />
            )}

            {isListMode && (
              <FormField
                name="sourceAttributeId"
                type={InputType.SELECT}
                inputProps={{
                  control,
                  error: errors?.sourceAttributeId,
                  label: t('labelValueList'),
                  required: true,
                  placeholder: t('labelValueList'),
                  options: sourceListOptions,
                  getValue: (list: IPartFinderAttribute) => list.id,
                  getLabel: getListLabel,
                }}
              />
            )}

            <FormField
              name="type"
              type={InputType.SELECT}
              inputProps={{
                control,
                error: errors?.type,
                label: t('labelType'),
                required: true,
                // A list's type is the list's, and a saved attribute keeps its own. Typed values
                // would be left behind by a switch, so they have to be removed first.
                disabled: isListMode || hasValues || isSavedAttribute,
                options: typeOptions,
                getValue: (option) => option.value,
                getLabel: (option) => option.label,
              }}
            />

            {(!isListMode || Boolean(selectedList)) && (
              <>
                {AttributeType.VALUES === type && <ValuesEditor onAdd={onAddValue} />}

                {AttributeType.COLOUR === type && <ColourEditor onAdd={onAddValue} />}

                {(isListMode || isInherited) && hasValues && (
                  <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                    {isInherited
                      ? t('labelValuesFromProductType', { name: inheritedSource.productTypeName })
                      : t('labelValuesFromList', {
                          name: selectedList?.name ?? attribute?.sourceAttributeName,
                        })}
                  </Typography>
                )}

                {hasValues && (
                  <AttributeValueRows
                    values={valueFields}
                    type={type}
                    onMove={move}
                    onRemove={remove}
                    getEndDecorator={
                      showsNewValues
                        ? (value) =>
                            isNewValue(value) && (
                              <Chip
                                size={Size.SM}
                                variant={Variant.SOFT}
                                color={Color.SUCCESS}
                                startDecorator={<Circle weight="fill" size={8} />}
                              >
                                {t('labelAttributeValueNew')}
                              </Chip>
                            )
                        : undefined
                    }
                  />
                )}

                {AttributeType.NUMBER_RANGE === type && (
                  <NumberRangeEditor hasRanges={hasValues} onAdd={onAddValue} />
                )}
              </>
            )}

            {showsNewValues && (
              <FormField
                name="updateList"
                type={InputType.CHECKBOX}
                inputProps={{
                  control,
                  label: t(
                    isInherited
                      ? 'labelUpdateProductTypeWithNewValue'
                      : 'labelUpdateListWithNewValue'
                  ),
                  disabled: !hasNewValues,
                }}
              />
            )}

            {!isListMode && !isInherited && hasValues && (
              <>
                {!isBannerDismissed && (
                  <Alert
                    variant={Variant.SOFT}
                    color={Color.PRIMARY}
                    startDecorator={<Info />}
                    endDecorator={
                      <IconButton
                        variant={Variant.PLAIN}
                        color={Color.PRIMARY}
                        size={Size.SM}
                        onClick={() => setIsBannerDismissed(true)}
                      >
                        <X />
                      </IconButton>
                    }
                  >
                    {t('messagePartFinderAttributeValuesLiveOnAttribute', {
                      count: valueFields.length,
                    })}
                  </Alert>
                )}

                <FormField
                  name="saveAsList"
                  type={InputType.CHECKBOX}
                  inputProps={{
                    control,
                    label: t('labelSaveValuesAsList'),
                  }}
                />
                <FormField
                  name="newListName"
                  type={InputType.TEXT_INPUT}
                  inputProps={{
                    control,
                    error: errors?.newListName,
                    label: t('labelNameValueList'),
                    disabled: !saveAsList,
                  }}
                />
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Form>
  );
};
